'use client';

import { useContext, useEffect, useRef } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import MobileShellContext from '@/components/Mobile/MobileShellContext';
import { ROUTES } from '@/lib/routes';
import { HOME_SLIDE, reachSlideIndex } from '@/components/Mobile/Home/slides';
import { useScrambleIn } from '@/lib/hooks/useScrambleIn';
import type { ProjectModel } from '@/lib/models/Project';
import styles from './directory-mobile.module.scss';

interface Props {
  projects: ProjectModel[];
}

interface ItemSpec {
  label: string;
  icon: string;
  slide?: number;
  href: string;
}

export default function DirectoryMobile({ projects }: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const { directoryOpen, closeDirectory, setSlide } = useContext(MobileShellContext);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const items: ItemSpec[] = [
    { label: 'ABOUT US', icon: '/assets/image/mobile/ABOUT US.svg', slide: HOME_SLIDE.intro, href: ROUTES.home },
    { label: 'CAPABILITIES', icon: '/assets/image/mobile/CAPABILITIES.svg', slide: HOME_SLIDE.loading, href: ROUTES.home },
    { label: 'OUR WORKS', icon: '/assets/image/mobile/OUR_WORKS.svg', slide: HOME_SLIDE.firstProject, href: ROUTES.home },
    // { label: 'LAB NOTES', icon: '/assets/image/mobile/LAB_NOTES.svg', href: ROUTES.blog },
    { label: 'CONTACT', icon: '/assets/image/mobile/CONTACT.svg', slide: reachSlideIndex(projects.length), href: ROUTES.home },
  ];

  useScrambleIn(modalRef, { active: directoryOpen, durationMs: [660, 660], uppercase: true });

  useEffect(() => {
    if (!directoryOpen) return;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDirectory();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      previouslyFocusedRef.current?.focus();
    };
  }, [directoryOpen, closeDirectory]);

  useEffect(() => {
    if (directoryOpen) closeDirectory();
  }, [pathname]);

  if (!directoryOpen) return null;

  const handleItemClick = (item: ItemSpec) => {
    if (typeof item.slide === 'number') setSlide(item.slide);
    closeDirectory();
    router.push(item.href);
  };

  return (
    <div
      className={styles.modalBackdrop}
      onClick={closeDirectory}
      role='presentation'
    >
      <div
        ref={modalRef}
        className={styles.modal}
        onClick={(event) => event.stopPropagation()}
        role='dialog'
        aria-modal='true'
        aria-label='Directory menu'
      >
        <div className={styles.modalHeader}>
          <img
            src='/assets/image/mobile/DIRECTORY.svg'
            alt=''
            aria-hidden='true'
            className={styles.modalIcon}
          />
          <h2 className={styles.modalTitle} data-scramble-in>DIRECTORY</h2>
          <button
            type='button'
            ref={closeButtonRef}
            className={styles.modalClose}
            onClick={closeDirectory}
            aria-label='Close directory'
          >
            <img
              src='/assets/image/mobile/WindowClose.svg'
              alt=''
              aria-hidden='true'
            />
          </button>
        </div>
        <ul className={styles.modalList}>
          {items.map((item) => (
            <li key={item.label}>
              <button
                type='button'
                className={styles.modalItem}
                onClick={() => handleItemClick(item)}
              >
                <img
                  src={item.icon}
                  alt=''
                  aria-hidden='true'
                  className={styles.modalItemIcon}
                />
                <span data-scramble-in>{item.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
